import { computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import {
    addColumnFilter,
    clearAllColumnFilters,
    clearColumnFilter,
    columnFilterReducer,
    filteredColumnIds,
    removeColumnFilter,
} from '@thinkalpha/ui-table/interactions/columnFilters';
import type { ColumnFilter, ColumnFilterAction, ColumnFilters } from '@thinkalpha/ui-table/interactions/columnFilters';

/**
 * Session-only per-column filters for one table (ADR 0014, Column Filter Contract). Composed at the
 * widget root so the grid header, the widget header summary, and the table query share one state.
 * Never persisted (AC-FILTER-004).
 */
export interface ColumnFiltersModelShape {
    readonly filters: ReadonlySignal<ColumnFilters<string>>;
    /** The grid's controlled header indicator (AC-FILTER-003). */
    readonly filteredColumnIds: ReadonlySignal<ReadonlySet<string>>;
    add(columnId: string, filter: ColumnFilter): void;
    remove(columnId: string, filter: ColumnFilter): void;
    clear(columnId: string): void;
    clearAll(): void;
}

export const ColumnFiltersModel: ModelConstructor<ColumnFiltersModelShape, []> = createModel<
    ColumnFiltersModelShape,
    []
>(() => {
    const filters = signal<ColumnFilters<string>>({});
    const dispatch = (action: ColumnFilterAction<string>) => {
        filters.value = columnFilterReducer(filters.value, action);
    };

    return {
        filters: computed(() => filters.value),
        filteredColumnIds: computed(() => filteredColumnIds(filters.value)),
        add: (columnId, filter) => dispatch(addColumnFilter(columnId, filter)),
        remove: (columnId, filter) => dispatch(removeColumnFilter(columnId, filter)),
        clear: (columnId) => dispatch(clearColumnFilter(columnId)),
        clearAll: () => dispatch(clearAllColumnFilters()),
    };
});

export type ColumnFiltersModel = Model<ColumnFiltersModelShape>;
